import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Search, Edit, Trash2, X, Check, Eye, CalendarPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usersService, UserListItem, packagesService, Package, getErrorMessage } from '@/services';
import dayjs from 'dayjs';

const userSchema = z.object({
  username: z.string().min(3, 'validation.minLength'),
  email: z.string().email('validation.email'),
  password: z.string().min(6, 'validation.minLength').optional().or(z.literal('')),
  is_active: z.boolean(),
  package_id: z.coerce.number().optional(),
});

type UserForm = z.infer<typeof userSchema>;

export default function AdminUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserListItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [extendingUser, setExtendingUser] = useState<number | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [datePickerUserId, setDatePickerUserId] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UserForm>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      is_active: true,
    },
  });

  const fetchData = async () => {
    try {
      const [usersResponse, packagesData] = await Promise.all([
        usersService.list(),
        packagesService.list(),
      ]);
      setUsers(usersResponse.users);
      setPackages(packagesData.filter(p => p.is_active === 1));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setEditingUser(null);
    const defaultPkg = packages.find(p => p.is_default === 1);
    reset({
      username: '',
      email: '',
      password: '',
      is_active: true,
      package_id: defaultPkg?.id,
    });
    setShowModal(true);
  };

  const openEditModal = (user: UserListItem) => {
    setEditingUser(user);
    reset({
      username: user.username,
      email: user.email || '',
      password: '',
      is_active: user.is_active === 1,
      package_id: user.package_id || undefined,
    });
    setShowModal(true);
  };

  const onSubmit = async (data: UserForm) => {
    try {
      const payload = {
        username: data.username,
        email: data.email,
        ...(data.password && { password: data.password }),
        is_active: data.is_active,
        package_id: data.package_id ?? undefined,
      };

      if (editingUser) {
        await usersService.update(editingUser.id, payload);
        toast.success(t('common.updated'));
      } else {
        if (!data.password) {
          toast.error(t('validation.required'));
          return;
        }
        await usersService.create({ ...payload, password: data.password! });
        toast.success(t('common.created'));
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await usersService.delete(id);
      toast.success(t('common.deleted'));
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleExtend = async (userId: number, days: number | null) => {
    try {
      if (days === null) {
        // Lifetime - remove expiry
        await usersService.update(userId, { expires_at: '' });
        toast.success(t('admin.lifetime'));
      } else {
        const result = await usersService.extendSubscription(userId, { days });
        toast.success(t('admin.extendSuccess', { date: dayjs(result.expires_at).format('DD/MM/YYYY') }));
      }
      setExtendingUser(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const openDatePicker = (userId: number) => {
    setDatePickerUserId(userId);
    setSelectedDate(dayjs().add(30, 'day').format('YYYY-MM-DD'));
    setExtendingUser(null);
    setShowDatePicker(true);
  };

  const handleExtendWithDate = async () => {
    if (!datePickerUserId || !selectedDate) return;
    try {
      await usersService.extendSubscription(datePickerUserId, { new_expires_at: selectedDate });
      toast.success(t('admin.dateSetSuccess', { date: dayjs(selectedDate).format('DD/MM/YYYY') }));
      setShowDatePicker(false);
      setDatePickerUserId(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.email && user.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('admin.users')}</h1>
          <p className="text-dark-400 mt-1">
            {users.length} {t('admin.totalUsers').toLowerCase()}
          </p>
        </div>
        <button onClick={openCreateModal} className="btn-primary">
          <Plus size={18} />
          {t('admin.addUser')}
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400"
        />
        <input
          type="text"
          placeholder={t('common.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="card">
          <p className="text-dark-400">{t('common.noData')}</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>{t('auth.username')}</th>
                <th>{t('auth.email')}</th>
                <th>{t('common.status')}</th>
                <th>{t('nav.packages')}</th>
                <th>{t('admin.expiresAt')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, index) => {
                // Dropdown opens down for first 3 rows, up for the rest
                const openUpward = index >= 3;
                return (
                  <tr key={user.id}>
                    <td className="font-medium text-dark-100">{user.username}</td>
                    <td>{user.email || '-'}</td>
                    <td>
                      <span
                        className={user.is_active ? 'badge-success' : 'badge-error'}
                      >
                        {user.is_active ? t('common.active') : t('admin.expired')}
                      </span>
                    </td>
                    <td>{user.role === 'admin' ? '-' : (user.package_name || '-')}</td>
                    <td>
                      {user.role === 'admin'
                        ? '-'
                        : user.expires_at
                          ? dayjs(user.expires_at).format('DD/MM/YYYY')
                          : t('admin.neverExpires')}
                    </td>
                    <td>
                      {deleteConfirm === user.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="p-1.5 rounded-lg hover:bg-primary-50 text-dark-400 hover:text-primary-600"
                            title={t('admin.viewDetail')}
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-100"
                          >
                            <Edit size={16} />
                          </button>
                          {/* Extend Subscription Dropdown */}
                          {user.role !== 'admin' && (
                            <div className="relative">
                              <button
                                onClick={() => setExtendingUser(extendingUser === user.id ? null : user.id)}
                                className="p-1.5 rounded-lg hover:bg-success-50 text-dark-400 hover:text-success-600"
                                title={t('admin.extendSubscription')}
                              >
                                <CalendarPlus size={16} />
                              </button>
                              {extendingUser === user.id && (
                                <div className={`absolute right-0 z-[100] bg-white border border-dark-600 rounded-lg shadow-lg py-1 min-w-[160px] ${openUpward ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
                                  <button
                                    onClick={() => handleExtend(user.id, 30)}
                                    className="w-full px-3 py-1.5 text-left text-sm text-dark-200 hover:bg-dark-700"
                                  >
                                    {t('admin.extend1Month')}
                                  </button>
                                  <button
                                    onClick={() => handleExtend(user.id, 90)}
                                    className="w-full px-3 py-1.5 text-left text-sm text-dark-200 hover:bg-dark-700"
                                  >
                                    {t('admin.extend3Months')}
                                  </button>
                                  <button
                                    onClick={() => handleExtend(user.id, 180)}
                                    className="w-full px-3 py-1.5 text-left text-sm text-dark-200 hover:bg-dark-700"
                                  >
                                    {t('admin.extend6Months')}
                                  </button>
                                  <button
                                    onClick={() => handleExtend(user.id, 365)}
                                    className="w-full px-3 py-1.5 text-left text-sm text-dark-200 hover:bg-dark-700"
                                  >
                                    {t('admin.extend1Year')}
                                  </button>
                                  <hr className="my-1 border-dark-600" />
                                  <button
                                    onClick={() => handleExtend(user.id, null)}
                                    className="w-full px-3 py-1.5 text-left text-sm text-primary-600 font-medium hover:bg-dark-700"
                                  >
                                    {t('admin.setLifetime')}
                                  </button>
                                  <button
                                    onClick={() => openDatePicker(user.id)}
                                    className="w-full px-3 py-1.5 text-left text-sm text-dark-200 hover:bg-dark-700"
                                  >
                                    {t('admin.setSpecificDate')}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          <button
                            onClick={() => setDeleteConfirm(user.id)}
                            className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div
            className="card w-full max-w-lg m-4 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-dark-100">
                {editingUser ? t('admin.editUser') : t('admin.addUser')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="label">{t('auth.username')}</label>
                <input
                  type="text"
                  className={errors.username ? 'input-error' : 'input'}
                  {...register('username')}
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-error-500">
                    {t(errors.username.message || '')}
                  </p>
                )}
              </div>

              <div>
                <label className="label">{t('auth.email')}</label>
                <input
                  type="email"
                  className={errors.email ? 'input-error' : 'input'}
                  {...register('email')}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-error-500">
                    {t(errors.email.message || '')}
                  </p>
                )}
              </div>

              <div>
                <label className="label">
                  {t('auth.password')}
                  {editingUser && (
                    <span className="text-dark-500 font-normal ml-2">
                      ({t('admin.leaveBlankToKeep')})
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  className={errors.password ? 'input-error' : 'input'}
                  {...register('password')}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-error-500">
                    {t(errors.password.message || '')}
                  </p>
                )}
              </div>

              {/* Package dropdown - only for non-admin users */}
              {editingUser?.role !== 'admin' && (
                <div>
                  <label className="label">{t('nav.packages')}</label>
                  <select
                    className="input"
                    {...register('package_id')}
                  >
                    <option value="">{t('common.select')}</option>
                    {packages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.name} {pkg.duration_days === 0 ? `(${t('packages.unlimited')})` : `(${pkg.duration_days} ${t('packages.days')})`}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-dark-500">
                    {t('packages.durationHelp')}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                  {...register('is_active')}
                />
                <label htmlFor="is_active" className="text-sm text-dark-200">
                  {t('common.active')}
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowDatePicker(false)}
        >
          <div
            className="card w-full max-w-sm m-4 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-dark-100">
                {t('admin.selectExpirationDate')}
              </h3>
              <button
                onClick={() => setShowDatePicker(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={dayjs().format('YYYY-MM-DD')}
                className="input w-full"
              />

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDatePicker(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleExtendWithDate}
                  disabled={!selectedDate}
                  className="btn-primary"
                >
                  {t('common.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
